import * as React from 'react';
import { useCallback, useState, useMemo } from 'react';
import { Str } from '../../../localization/translationKeys';
import LocalTextArea from '../../../localization/LocalTextArea';
import LocalBtn from '../../../localization/LocalBtn';
import { FLEX_ROW, FLEX_ADAPTIVE } from '../../../layout/flexBox';
import { COLORS } from '../../../globalStyle/colors';
import { css } from '@emotion/core';
import Spacer from '../../../layout/Spacer';
import { btnStyle } from '../../../forms/styles/button';
import { TEXT_AREA } from '../../../forms/styles/textarea';
import FaceIcon from '../../../icons/FaceIcon';
import { useLoading } from '../../../core/api/hooks';
import { ActionType } from '../../../core/redux/actionType';
import { useOnSuccess } from '../../../core/hooks/useOnSuccess';


const styles = {
  root: [
    FLEX_ROW,
    css`
      align-items: flex-start;
    `,
  ],
  form: {
    root:      [
      FLEX_ADAPTIVE,
      css`
        align-items:flex-start;
        flex-basis: 100%;
      `,
    ],
    textArea:  [
      TEXT_AREA,
      css`
        width: 100%;
        font-size: 12px;
        ::placeholder {
          font-style: italic;
        }
      `,
    ],
    submitBtn: [
      btnStyle.primary(COLORS.BLUE),
    ],
  },
};


type Props = {
  projectId: number;
  devItemId: number;
  devActionId: number;
  addComment: (comment: string) => void;
  onCommentAdded: (comment: string) => void;
  className?: string;
};


/**
 * AddDevItemCommentForm: a component that allows collaborators and participants to add comments to dev item actions
 */
const AddDevItemCommentForm: React.FC<Props> = (
  {
    projectId,
    devItemId,
    devActionId,
    addComment,
    onCommentAdded,
    className,
  },
) => {
  const [comment, setComment] = useState<string | undefined>(undefined);

  const onClickAddBtn = useCallback(
    event => {
      event.preventDefault();
      addComment(comment!);
    },
    [addComment, comment],
  );

  const onChangeComment = useCallback(event => setComment(event.target.value), []);

  const addCommentReqId = useMemo(
    () => `${projectId}-${devItemId}-${devActionId}`,
    [devActionId, devItemId, projectId],
  );

  const addingComment = useLoading(ActionType.AddDevItemCommentRequest, addCommentReqId);

  useOnSuccess(
    () => {
      onCommentAdded(comment!);
      setComment('');
    },
    ActionType.AddDevItemCommentRequest,
    addCommentReqId,
  );

  return (
    <div className={className} css={styles.root}>
      <FaceIcon sizePx={30} />
      <Spacer sizePx={12} />
      <form css={styles.form.root}>
        <LocalTextArea
          value={comment}
          placeholderTranslation={Str.EnterComment}
          onChange={onChangeComment}
          disabled={addingComment}
          css={styles.form.textArea}
        />
        <Spacer sizePx={12} />
        <LocalBtn
          str={Str.Add}
          onClick={onClickAddBtn}
          disabled={!comment || addingComment}
          css={styles.form.submitBtn}
        />
      </form>
    </div>
  );
};

export default AddDevItemCommentForm;
